import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Row, Select } from "antd";
import PageHeader from "../components/PageHeader";
import AccountSummary from "../components/AccountSummary";
import InvoiceTable from "../components/InvoiceTable";
import SavedCards from "../components/SavedCards";
import apiService from "../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";
import "./billing.css";
import ConfigurationConstants from "../../experience/utils/configurationconstants";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { currentConfiguration } from "../../service/memory/recoil/configurationstore";
import { useReadMemory } from "../../experience/generic/hooks/useReadMemory";
import MemoryType from "../../service/memory/enum/MemoryType";
import { userDetails } from "../../components/recoil/userDataRecoil";
import PrimaryButton from "../../components/generic/buttons/PrimaryButton";
import { clearLocalStorage } from "../../experience/auth/logout/utils";
import { publishEvent } from "../../components/generic/EventPublisher";
import { useNavigate } from "react-router";

const LG_MIN = 992;

const BillingDashboard = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userData = useReadMemory(MemoryType.USER)
  const setUserDetails = useSetRecoilState(userDetails)
  const navigate = useNavigate()
  const [signOutLoading, setSignOutLoading] = useState(false)
  const configuration = useRecoilValue(currentConfiguration);
  const [billingDashboardRefreshKey, setBillingDashboardRefreshKey] =
    useState(0);
  const [offices, setOffices] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [selectedOfficeId, setSelectedOfficeId] = useState(null);
  const mainSummaryMeasureRef = useRef(null);
  const [sidebarMatchHeightPx, setSidebarMatchHeightPx] = useState(null);

  const isOfficeSelectAllowed = useMemo(
    () =>
      Boolean(
        configuration[ConfigurationConstants.FEATUREACCESS_BILLING_OFFICESELECT]
          ?.visibility,
      ),
    [configuration],
  );

  const handleLogout = async () => {
    setSignOutLoading(true);
    const deleteEndpoint = "event/device/v1/" + userData?.email || "";
    await apiService.delete(deleteEndpoint);
    publishEvent("niramaya.user.logout", userData);
    clearLocalStorage();
    setUserDetails({});
    setSignOutLoading(false);
    navigate("/login");
  };

  useEffect(() => {
    if (!isOfficeSelectAllowed) {
      setOffices([]);
      setSelectedOfficeId(null);
      setOfficesLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadOffices = async () => {
      setOfficesLoading(true);
      try {
        const response = await apiService.get(
          "niramayafoundation/niramayaoffice/api/v1/offices",
          { organizationIdentifier },
        );
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        setOffices(list);
        setSelectedOfficeId((current) => {
          if (current != null && list.some((office) => office.id === current)) {
            return current;
          }
          return list[0]?.id ?? null;
        });
      } finally {
        if (!cancelled) {
          setOfficesLoading(false);
        }
      }
    };

    loadOffices();

    return () => {
      cancelled = true;
    };
  }, [isOfficeSelectAllowed, organizationIdentifier]);

  const refreshBillingDashboard = useCallback(() => {
    setBillingDashboardRefreshKey((k) => k + 1);
  }, []);

  useLayoutEffect(() => {
    const el = mainSummaryMeasureRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const mq = window.matchMedia(`(min-width: ${LG_MIN}px)`);

    const measure = () => {
      if (!mq.matches) {
        setSidebarMatchHeightPx(null);
        return;
      }
      const h = Math.round(el.getBoundingClientRect().height);
      setSidebarMatchHeightPx(Number.isFinite(h) && h > 0 ? h : null);
    };

    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(measure);
    });

    ro.observe(el);
    mq.addEventListener("change", measure);
    measure();

    return () => {
      ro.disconnect();
      mq.removeEventListener("change", measure);
    };
  }, [billingDashboardRefreshKey]);

  const sidebarColClassName = [
    "billing-dashboard-sidebar",
    sidebarMatchHeightPx != null ? "billing-dashboard-sidebar--sized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const sidebarColStyle =
    sidebarMatchHeightPx != null
      ? { "--billing-sidebar-match-height": `${sidebarMatchHeightPx}px` }
      : undefined;

  const billingOfficeId = !isOfficeSelectAllowed
    ? undefined
    : offices.length === 0
      ? undefined
      : officesLoading
        ? null
        : selectedOfficeId;

  const showOfficeSelect =
    isOfficeSelectAllowed && (officesLoading || offices.length > 0);

  const officeSelectActions = showOfficeSelect ? (
    <div className="billing-office-select-row">
      <label
        className="billing-office-select-label"
        htmlFor="billing-office-select"
      >
        Office:
      </label>
      <Select
        loading={officesLoading}
        id="billing-office-select"
        className="billing-office-select"
        value={selectedOfficeId}
        placeholder="Select office"
        onChange={setSelectedOfficeId}
        options={offices.map((office) => ({
          value: office.id,
          label: office.name,
        }))}
      />
    </div>
  ) : null;

  const billingAdminLogout =
    userData?.jobTitle === "Billing Admin" ? (
      <div className="billing-office-select-row">
        <PrimaryButton
          buttonText="Sign Out"
          loading={signOutLoading}
          handleOnClick={handleLogout}
        />
      </div>
    ) : null;

  return (
    <section className="section-content">
      <PageHeader title="Billing" actions={officeSelectActions} billingAdminLogout={billingAdminLogout} userData={userData} />
      <div className="billing-dashboard-content">
        <Row gutter={[24, 24]} className="billing-dashboard-top" align="top">
          <Col xs={24} lg={16} className="billing-dashboard-main">
            <div
              ref={mainSummaryMeasureRef}
              className="billing-dashboard-main-measure"
            >
              <AccountSummary
                refreshKey={billingDashboardRefreshKey}
                officeId={billingOfficeId}
              />
            </div>
          </Col>
          <Col
            xs={24}
            lg={8}
            className={sidebarColClassName}
            style={sidebarColStyle}
          >
            <SavedCards officeId={billingOfficeId} />
          </Col>
        </Row>
        <InvoiceTable
          onPaymentSuccess={refreshBillingDashboard}
          officeId={billingOfficeId}
        />
      </div>
    </section>
  );
};

export default BillingDashboard;
